import { X } from '@phosphor-icons/react';
import React, { useId } from 'react';
import { Badge } from 'src/components/Badge';
import { IconButton } from 'src/components/IconButton';
import { Input } from 'src/components/Input';
import { SelectBox } from 'src/components/SelectBox';
import {
  getOperatorLabel,
  getOperatorOptionsForInsightType,
  sortInsightsForSelect,
} from 'src/components/insights/insightFilterRules';
import {
  FieldOperators,
  Insight,
  InsightType,
} from 'src/interfaces/agent.interface';

type InsightFilterRowProps = {
  insights: Insight[];
  selectedInsightId: string;
  operator: FieldOperators | undefined;
  value: string | boolean | null;
  onInsightChange: (insightId: string) => void;
  onOperatorChange: (operator: FieldOperators) => void;
  onValueChange: (value: string | boolean | null) => void;
  onDelete: () => void;
  disabled?: boolean;
  badgeLabel?: string;
  deleteAriaLabel?: string;
};

export const InsightFilterRow: React.FC<InsightFilterRowProps> = ({
  insights,
  selectedInsightId,
  operator,
  value,
  onInsightChange,
  onOperatorChange,
  onValueChange,
  onDelete,
  disabled = false,
  badgeLabel = 'Call Insight',
  deleteAriaLabel = 'Remove insight filter',
}) => {
  const rowId = useId();
  const sortedInsights = sortInsightsForSelect(insights);
  const selectedInsight =
    insights.find((insight) => insight.id === selectedInsightId) ?? null;
  const selectedInsightType = selectedInsight?.type;
  const operatorOptions = getOperatorOptionsForInsightType(selectedInsightType);

  const insightOptions = sortedInsights.map((insight) => {
    const trimmedLabel = insight.label?.trim();

    if (!trimmedLabel || trimmedLabel === insight.id) {
      return {
        value: insight.id,
        label: insight.id,
      };
    }

    return {
      value: insight.id,
      label: trimmedLabel,
      secondaryLabel: insight.id,
    };
  });

  const selectedInsightOption = selectedInsightId
    ? (insightOptions.find((option) => option.value === selectedInsightId) ?? {
        value: selectedInsightId,
        label: selectedInsightId,
      })
    : null;

  const renderValueInput = () => {
    if (!selectedInsight) {
      return (
        <Input
          id={`${rowId}-insight-value-placeholder`}
          placeholder="Value"
          value=""
          disabled
        />
      );
    }

    if (selectedInsightType === InsightType.BOOLEAN) {
      return (
        <SelectBox
          value={
            typeof value === 'boolean'
              ? {
                  value: value ? 'true' : 'false',
                  label: value ? 'Yes' : 'No',
                }
              : null
          }
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          onChange={(nextValue) => {
            if (nextValue !== 'true' && nextValue !== 'false') {
              onValueChange(null);
              return;
            }
            onValueChange(nextValue === 'true');
          }}
          disabled={disabled}
        />
      );
    }

    if (selectedInsightType === InsightType.ENUM) {
      const enumOptions = (selectedInsight.enum_options ?? [])
        .slice()
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
        .map((option) => ({
          value: option,
          label: option,
        }));

      return (
        <SelectBox
          value={
            typeof value === 'string' && value
              ? {
                  value,
                  label: value,
                }
              : null
          }
          options={enumOptions}
          onChange={(nextValue) => {
            if (!nextValue) {
              onValueChange(null);
              return;
            }
            onValueChange(String(nextValue));
          }}
          disabled={disabled || enumOptions.length === 0}
        />
      );
    }

    return (
      <Input
        id={`${rowId}-insight-value`}
        placeholder={
          selectedInsightType === InsightType.NUMBER
            ? 'Enter number'
            : 'Value to compare'
        }
        type={selectedInsightType === InsightType.NUMBER ? 'number' : 'text'}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onValueChange(event.target.value)}
        disabled={disabled}
      />
    );
  };

  return (
    <div className="rounded-lg border border-neutral-200 p-4 bg-neutral-0 flex items-center gap-3">
      <Badge
        variant="primary"
        className="text-md px-4 font-normal whitespace-nowrap"
      >
        {badgeLabel}
      </Badge>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center flex-1">
        <div>
          <SelectBox
            value={selectedInsightOption}
            options={insightOptions}
            onChange={(nextValue) =>
              onInsightChange(nextValue ? String(nextValue) : '')
            }
            disabled={disabled || insightOptions.length === 0}
            placeholder="Select insight"
          />
        </div>

        <div>
          <SelectBox
            value={
              operator
                ? {
                    value: operator,
                    label: getOperatorLabel(operator, selectedInsightType),
                  }
                : null
            }
            options={operatorOptions}
            onChange={(nextValue) => {
              if (!nextValue) return;
              onOperatorChange(nextValue as FieldOperators);
            }}
            disabled={disabled || !selectedInsight}
            placeholder="Operator"
          />
        </div>

        <div>{renderValueInput()}</div>
      </div>

      <IconButton
        aria-label={deleteAriaLabel}
        onClick={onDelete}
        disabled={disabled}
        className="hover:text-error"
      >
        <X size={16} />
      </IconButton>
    </div>
  );
};
